import React, { useEffect, useState } from 'react'
import { usePopper } from 'react-popper'

import cn from '~/lib/cn'
import Icon from '~/truck/icon'
import type { IProduct } from '~/types/products'

import { VariantsStockTable } from './variants-stock-table'

interface VariantsStockPopoverProps {
  variants: IProduct[]
  activeVariant?: IProduct
}

function VariantsStockPopover({
  variants,
  activeVariant,
  triggerElement,
  onClose,
}: VariantsStockPopoverProps & {
  triggerElement: HTMLButtonElement | null
  onClose: () => void
}) {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)

  const { styles, attributes } = usePopper(triggerElement, popperElement, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 16,
        },
      },
    ],
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  useEffect(() => {
    if (!triggerElement || !popperElement) return

    const onClickOutside = (event: MouseEvent) => {
      const target = event.composedPath?.()?.[0] || event.target

      if (target instanceof Node) {
        if (!popperElement.contains(target) && !triggerElement.contains(target)) {
          onClose()
        }
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [triggerElement, popperElement, onClose])

  return (
    <div
      ref={setPopperElement}
      style={styles.popper}
      {...attributes.popper}
      className={cn(
        'z-50 rounded-lg bg-white p-3 shadow-lg',
        'ring-1 ring-black/5',
        'flex max-h-80 max-w-[calc(100vw-2rem)] flex-col gap-2',
      )}
    >
      <div className="flex shrink-0 items-center justify-between">
        <span className="text-sm font-semibold">Stock por variante</span>
        <button type="button" onClick={onClose}>
          <Icon type="close" size="sm" color="grayscale80" />
        </button>
      </div>
      <VariantsStockTable variants={variants} activeVariant={activeVariant} />
    </div>
  )
}

export default VariantsStockPopover
